import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

const AuthContext = createContext();

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};

export const AuthProvider = ({ children }) => {
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [token, setToken] = useState(null);
    const [refreshToken, setRefreshToken] = useState(null);
    const [user, setUser] = useState(null); // Store complete user data
    const [loading, setLoading] = useState(true);
    const [lastActivity, setLastActivity] = useState(Date.now());    const [sessionWarning, setSessionWarning] = useState(false);
    const [sessionTimeRemaining, setSessionTimeRemaining] = useState(null);
    
    // Session timeout in minutes (adjust as needed)
    const SESSION_TIMEOUT = 30;
    const TOKEN_REFRESH_THRESHOLD = 5; // Refresh token 5 minutes before expiry
    const SESSION_WARNING_THRESHOLD = 5; // Warn user 5 minutes before session expires
    
    useEffect(() => {
        const storedToken = localStorage.getItem('jwtToken');
        const storedRefreshToken = localStorage.getItem('refreshToken');
        const storedUser = localStorage.getItem('userData');
        
        if (storedToken && storedRefreshToken) {
            setToken(storedToken);
            setRefreshToken(storedRefreshToken);
            if (storedUser) {
                try {
                    setUser(JSON.parse(storedUser));
                } catch (error) {
                    console.error('Error parsing stored user data:', error);
                    localStorage.removeItem('userData');
                }
            }            setIsAuthenticated(true);
            setLastActivity(Date.now());
        }
        setLoading(false);
    }, []);
    
    // Track user activity
    const updateActivity = useCallback(() => {
        setLastActivity(Date.now());
    }, []);
    
    // Check if session is expired based on inactivity
    const isSessionExpired = useCallback(() => {
        const timeSinceLastActivity = Date.now() - lastActivity;
        return timeSinceLastActivity > (SESSION_TIMEOUT * 60 * 1000);
    }, [lastActivity]);

    // Check if session warning should be shown
    const shouldShowSessionWarning = useCallback(() => {
        const timeSinceLastActivity = Date.now() - lastActivity;
        const warningThreshold = (SESSION_TIMEOUT - SESSION_WARNING_THRESHOLD) * 60 * 1000;
        return timeSinceLastActivity > warningThreshold && timeSinceLastActivity < (SESSION_TIMEOUT * 60 * 1000);
    }, [lastActivity]);

    // Get remaining session time in minutes
    const getRemainingSessionTime = useCallback(() => {
        const timeSinceLastActivity = Date.now() - lastActivity;
        const totalTimeout = SESSION_TIMEOUT * 60 * 1000;
        const remaining = Math.max(0, totalTimeout - timeSinceLastActivity);
        return Math.ceil(remaining / (60 * 1000)); // Return minutes
    }, [lastActivity]);

    // Extend session (reset activity timer)
    const extendSession = useCallback(() => {
        setLastActivity(Date.now());
        setSessionWarning(false);
        console.log('Session extended by user action');
    }, []);

    // Check if token is about to expire (JWT expiry check)
    const isTokenNearExpiry = useCallback((token) => {
        if (!token) return true;
        
        try {
            const payload = JSON.parse(atob(token.split('.')[1]));
            const currentTime = Date.now() / 1000;
            const tokenExpiry = payload.exp;
            
            // Check if token expires within the threshold
            return (tokenExpiry - currentTime) < (TOKEN_REFRESH_THRESHOLD * 60);
        } catch (error) {
            console.error('Error parsing token:', error);
            return true;
        }
    }, []);

    // Refresh access token
    const refreshAccessToken = useCallback(async () => {
        if (!refreshToken) {
            logout();
            return null;
        }

        try {            
            const response = await fetch('http://localhost:8000/api/auth/refresh-token', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ refreshToken }),
            });

            const data = await response.json();

            if (response.ok && data.data) {
                const newToken = data.data.token;
                const newUser = data.data.user;
                console.log("newToken ====>"+newToken);
                localStorage.setItem('jwtToken', newToken);
                if (newUser) {
                    localStorage.setItem('userData', JSON.stringify(newUser));
                    setUser(newUser);
                }
                setToken(newToken);
                setLastActivity(Date.now());
                
                return newToken;
            } else {
                console.error('Failed to refresh token:', data.message);
                logout();
                return null;
            }
        } catch (error) {
            console.error('Error refreshing token:', error);
            logout();
            return null;
        }
    }, [refreshToken]);

    // Activity monitoring
    useEffect(() => {
        if (!isAuthenticated) return;

        const events = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click'];
        
        const activityHandler = () => {
            updateActivity();
        };

        events.forEach(event => {
            document.addEventListener(event, activityHandler, true);
        });

        // Check session expiry every minute
        const sessionCheckInterval = setInterval(() => {
            if (isSessionExpired()) {
                console.log('Session expired due to inactivity');
                logout();
            }
        }, 60000);

        // Check token expiry and refresh if needed
        const tokenCheckInterval = setInterval(async () => {
            if (token && isTokenNearExpiry(token)) {
                console.log('Token near expiry, attempting refresh...');
                await refreshAccessToken();
            }
        }, 60000);

        // Warn user before session expires
        const warningCheckInterval = setInterval(() => {
            if (isAuthenticated && !sessionWarning && token && isTokenNearExpiry(token)) {
                const timeLeft = (JSON.parse(atob(token.split('.')[1])).exp * 1000) - Date.now();
                if (timeLeft <= (SESSION_WARNING_THRESHOLD * 60 * 1000)) {
                    setSessionWarning(true);
                    setSessionTimeRemaining(Math.ceil(timeLeft / 1000 / 60)); // in minutes
                }
            }
        }, 60000);

        return () => {
            events.forEach(event => {
                document.removeEventListener(event, activityHandler, true);
            });
            clearInterval(sessionCheckInterval);
            clearInterval(tokenCheckInterval);
            clearInterval(warningCheckInterval);        };
    }, [isAuthenticated, token, isSessionExpired, isTokenNearExpiry, refreshAccessToken, updateActivity, sessionWarning]);    // Role-based access control functions (backend-dependent)
    const hasRole = (role) => {
        // console.log('hasRole check:', {
        //     role,
        //     user,
        //     userRoles: user?.roles,
        //     hasUser: !!user,
        //     hasRoles: !!(user && user.roles)
        // });
        if (!user || !user.roles) return false;
        const result = user.roles.some(userRole => 
            userRole.toLowerCase() === role.toLowerCase()
        );
        // console.log('hasRole result:', result);
        return result;
    };

    const hasAnyRole = (roles) => {
        if (!user || !user.roles) return false;
        return roles.some(role => hasRole(role));
    };

    const hasGroup = (group) => {
        if (!user || !user.groups) return false;
        return user.groups.some(userGroup => 
            userGroup.toLowerCase() === group.toLowerCase()
        );
    };

    const hasAnyGroup = (groups) => {
        if (!user || !user.groups) return false;
        return groups.some(group => hasGroup(group));
    };    const isAdmin = () => hasRole('ADMIN');
    const isStaff = () => hasRole('STAFF');
    const isStudent = () => hasRole('STUDENT');
    const isApplicant = () => hasRole('APPLICANT');

    const getUserType = () => user?.userType || 'USER';
    const getUserFullName = () => user?.fullName || user?.username || 'Unknown User';
    const getUserEmail = () => user?.email || '';
    const getUserRoles = () => user?.roles || [];
    const getUserGroups = () => user?.groups || [];    const login = (accessToken, refreshTokenValue, userData) => {
        console.log('AuthContext login called with:', {
            token: accessToken,
            refreshToken: refreshTokenValue,
            userData: userData
        });
        
        localStorage.setItem('jwtToken', accessToken);
        localStorage.setItem('refreshToken', refreshTokenValue);
        if (userData) {
            localStorage.setItem('userData', JSON.stringify(userData));
            setUser(userData);
            console.log('User data stored in state:', userData);
        }
        setToken(accessToken);
        setRefreshToken(refreshTokenValue);
        setIsAuthenticated(true);
        setLastActivity(Date.now());
        
        console.log('Login completed, isAuthenticated should now be:', true);
    };const logout = useCallback((userType = null) => {
        localStorage.removeItem('jwtToken');
        localStorage.removeItem('refreshToken');
        localStorage.removeItem('userData');
        setToken(null);
        setRefreshToken(null);
        setUser(null);
        setIsAuthenticated(false);
        setLastActivity(0);
        setSessionWarning(false);
        setSessionTimeRemaining(null);
        
        // Determine redirect URL based on user type
        let redirectUrl = '/login'; // Default to staff login
        
        // Check current user type or passed parameter
        const currentUserType = userType || user?.userType || getUserType();
        
        if (currentUserType === 'APPLICANT' || user?.roles?.includes('APPLICANT')) {
            redirectUrl = '/applicant/login';
        }
        
        // Redirect to appropriate login page
        window.location.href = redirectUrl;
    }, [user]);const getAuthHeaders = () => {
        const currentToken = token || localStorage.getItem('jwtToken');
        return {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${currentToken}`
        };
    };

    const getFormDataHeaders = () => {
        const currentToken = token || localStorage.getItem('jwtToken');
        return {
            'Authorization': `Bearer ${currentToken}`
        };
    };    // Enhanced API call function with automatic token refresh
    const makeAuthenticatedRequest = useCallback(async (url, options = {}) => {
        let currentToken = token || localStorage.getItem('jwtToken');

        // Check if token needs refresh before making the request
        if (currentToken && isTokenNearExpiry(currentToken)) {
            console.log('Token needs refresh before request');
            currentToken = await refreshAccessToken();
            if (!currentToken) {
                throw new Error('Unable to refresh token');
            }
        }        // Update activity on API call
        updateActivity();

        // Debug logging
        console.log('Making authenticated request to:', url);
        console.log('Token from state:', token ? token.substring(0, 20) + '...' : 'null');
        console.log('Token from localStorage:', localStorage.getItem('jwtToken') ? localStorage.getItem('jwtToken').substring(0, 20) + '...' : 'null');
        console.log('Using token:', currentToken ? currentToken.substring(0, 20) + '...' : 'null');

        let headers = {
            'Authorization': `Bearer ${currentToken}`,
            ...options.headers
        };
        
        // Only add Content-Type: application/json if we're not using FormData
        if (!(options.body instanceof FormData)) {
            headers['Content-Type'] = 'application/json';
        }try {
            const response = await fetch(url, {
                ...options,
                headers: headers
            });

            // If we get 401/403, try to refresh token once
            if ((response.status === 401 || response.status === 403) && refreshToken) {
                console.log('Got 401/403, attempting token refresh...');
                const newToken = await refreshAccessToken();
                
                if (newToken) {                    // Retry the request with new token
                    const retryHeaders = {
                        ...headers,
                        'Authorization': `Bearer ${newToken}`
                    };
                    
                    // Ensure we're not overriding Content-Type for FormData
                    if (options.body instanceof FormData) {
                        delete retryHeaders['Content-Type'];
                    }
                    
                    const retryResponse = await fetch(url, {
                        ...options,
                        headers: retryHeaders
                    });
                    
                    // Parse JSON response for retry
                    if (retryResponse.ok) {
                        return await retryResponse.json();
                    } else {
                        const errorData = await retryResponse.json();
                        throw new Error(errorData.message || 'Request failed after token refresh');
                    }
                }
            }            // Parse JSON response for initial request
            if (response.ok) {
                return await response.json();
            } else {
                const errorData = await response.json().catch(() => ({ 
                    message: 'Request failed', 
                    status: response.status,
                    statusText: response.statusText 
                }));
                
                // Enhance error with status code for better handling in components
                const error = new Error(errorData.message || `HTTP ${response.status}: ${response.statusText}`);
                error.status = response.status;
                error.statusText = response.statusText;
                error.responseData = errorData;
                
                throw error;
            }
        } catch (error) {
            console.error('Error in authenticated request:', error);
            throw error;
        }
    }, [token, refreshToken, isTokenNearExpiry, refreshAccessToken, updateActivity]);    const value = {
        isAuthenticated,
        token,
        refreshToken,
        user,
        login,
        logout,
        getAuthHeaders,
        getFormDataHeaders,
        makeAuthenticatedRequest,
        loading,
        lastActivity,
        SESSION_TIMEOUT,
        sessionWarning,
        sessionTimeRemaining,
        extendSession,
        getRemainingSessionTime,
        // Role-based access control functions
        hasRole,
        hasAnyRole,
        hasGroup,
        hasAnyGroup,
        isAdmin,
        isStaff,
        isStudent,
        isApplicant,
        getUserType,        
        getUserFullName,
        getUserEmail,
        getUserRoles,
        getUserGroups
    };
    
    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    );
};

// export { AuthProvider };
export default useAuth;
